import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { UploadCloud, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, Users, FileSpreadsheet, Download, Filter } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  ["prefix", "Prefix (Mr./Mrs./Dr.)"],
  ["first_name", "First Name"], ["last_name", "Last Name"], ["preferred_name", "Preferred Name"],
  ["former_name", "Former/Maiden Name"], ["birthdate", "Birthdate"], ["class_year", "Class Year"],
  ["year_graduated", "Year Graduated"], ["year_entered", "Year Entered"], ["living_status", "Living Status"],
  ["email", "Email"], ["secondary_email", "Secondary Email"], ["phone", "Phone"], ["cell_phone", "Cell / Mobile Phone"],
  ["address_line1", "Address"], ["city", "City"],
  ["state", "State"], ["postal_code", "ZIP / Postal Code"], ["country", "Country"], ["grad_year", "Grad Year"], ["degree", "Degree"],
  ["major", "Major"], ["minor", "Minor"], ["employer", "Employer"], ["job_title", "Job Title"], ["industry", "Industry"],
  ["addressee", "Addressee"], ["salutation", "Salutation"], ["solicit_code", "Personal Solicit Code"], ["last_gift_date", "Last Gift Date"],
];

const ALIASES = {
  birthdate: ["birthday", "dob", "dateofbirth", "borndate"],
  solicit_code: ["personsolicitcode", "solicitcode", "solicit"],
  last_gift_date: ["lastgiftdate", "lastgift", "lastgiftdt"],
  cell_phone: ["cell", "mobile", "cellphone", "mobilephone", "cellphonenumber"],
  secondary_email: ["secondaryemail", "altemail", "alternateemail", "email2", "otheremail"],
  postal_code: ["zip", "zipcode", "postalcode", "postal"],
  job_title: ["title", "jobtitle", "position"],
  address_line1: ["address", "addressline1", "street", "mailingaddress"],
  preferred_name: ["nickname", "preferredname", "goesby"],
  former_name: ["maidenname", "maiden", "formername"],
  prefix: ["prefix", "title", "salutationprefix"],
};

const autoMatch = (columns) => {
  const map = {};
  const used = new Set();
  FIELDS.forEach(([field]) => {
    const fn = field.replace(/_/g, "");
    const aliases = ALIASES[field] || [];
    const found = columns.find((c) => {
      if (used.has(c)) return false;
      const cn = c.toLowerCase().replace(/[^a-z0-9]/g, "");
      return cn === fn || cn.includes(fn) || aliases.some((a) => cn === a || cn.includes(a));
    });
    if (found) { map[field] = found; used.add(found); }
  });
  return map;
};

export default function Import() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({ class_year: "", state: "", group_id: "", q: "" });
  const [classYears, setClassYears] = useState([]);
  const [states, setStates] = useState([]);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    api.get("/alumni/filters").then((r) => { setClassYears(r.data.class_years || []); setStates(r.data.states || []); }).catch(() => {});
    api.get("/groups").then((r) => setGroups(r.data.groups || [])).catch(() => {});
  }, []);

  const hasFilters = !!(filters.class_year || filters.state || filters.group_id || filters.q.trim());
  const clearFilters = () => setFilters({ class_year: "", state: "", group_id: "", q: "" });

  const downloadTemplate = () => {
    const SAMPLE = {
      prefix: "Dr.", first_name: "Jane", last_name: "Doe", preferred_name: "Janie", former_name: "Smith",
      birthdate: "1976-04-12", class_year: "1998", year_graduated: "1998", year_entered: "1994", living_status: "Living",
      email: "jane.doe@example.com", secondary_email: "jane.alt@example.com", phone: "(555) 123-4567", cell_phone: "(555) 987-6543",
      address_line1: "123 Main St", city: "Dallas", state: "TX", postal_code: "75062", country: "USA",
      grad_year: "1998", degree: "BA", major: "English", minor: "History", employer: "Acme Corp", job_title: "Director", industry: "Technology",
      addressee: "Dr. Jane Doe", salutation: "Jane", solicit_code: "AN01", last_gift_date: "03/15/2024",
    };
    const headers = FIELDS.map(([, label]) => label);
    const sample = FIELDS.map(([field]) => SAMPLE[field] || "");
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [headers.map(esc).join(","), sample.map(esc).join(",")].join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "alumni-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Template downloaded — fill it in or map your export to these columns");
  };

  const exportAlumni = async () => {
    setBusy("export");
    try {
      const params = {};
      if (filters.class_year) params.class_year = filters.class_year;
      if (filters.state) params.state = filters.state;
      if (filters.group_id) params.group_id = filters.group_id;
      if (filters.q.trim()) params.q = filters.q.trim();
      const r = await api.get("/export/alumni", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `alumni-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(hasFilters ? "Filtered alumni exported to CSV" : "Alumni exported to CSV");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Export failed"); }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(data);
      setMapping(autoMatch(data.columns));
      setStep(2);
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const validate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/validate", { import_id: parsed.import_id, mapping });
      setValidation(data);
      setStep(3);
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/commit", { import_id: parsed.import_id, mapping, skip_missing_names: true });
      toast.success(`Imported ${data.imported} records successfully`);
      if (data.geocoding > 0) toast.info(`Mapping ${data.geocoding} addresses in the background — the map will fill in shortly.`);
      navigate("/alumni");
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fade-up max-w-4xl">
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Data Import</div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight mt-1">Import Alumni Spreadsheet</h1>
      </div>

      <div className="flex items-center gap-2 mb-8">
        {["Upload", "Map Columns", "Review & Import"].map((label, i) => (
          <React.Fragment key={label}>
            <div className={`flex items-center gap-2 ${step >= i + 1 ? "text-primary" : "text-muted-foreground"}`}>
              <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= i + 1 ? "bg-primary text-white" : "bg-muted"}`}>{i + 1}</div>
              <span className="text-sm font-medium hidden sm:block">{label}</span>
            </div>
            {i < 2 && <div className={`flex-1 h-px ${step > i + 1 ? "bg-primary" : "bg-border"}`} />}
          </React.Fragment>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 bg-card border border-border rounded-lg p-4">
            <div className="text-sm text-muted-foreground max-w-xl">
              New here? Download the template first, fill it with your alumni (or map your system's export to these columns), then upload it below. You can also <b>export</b> your current alumni to a CSV (same columns) to edit and re-import.
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid="export-alumni" onClick={exportAlumni} disabled={busy} className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60">
                <FileSpreadsheet className="h-4 w-4" /> {busy === "export" ? "Exporting…" : (hasFilters ? "Export filtered" : "Export alumni")}
              </button>
              <button data-testid="download-template" onClick={downloadTemplate} className="flex items-center gap-2 px-4 py-2 rounded-md border border-primary text-primary text-sm font-medium hover:bg-primary/5 transition-colors">
                <Download className="h-4 w-4" /> Download template
              </button>
            </div>
          </div>

          <div data-testid="export-filters" className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Export filters</span>
              <span className="text-xs text-muted-foreground">— narrow the export to a class year, chapter, state, or keyword. Leave blank to export everyone.</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">Class year</label>
                <select data-testid="filter-class-year" value={filters.class_year} onChange={(e) => setFilters((f) => ({ ...f, class_year: e.target.value }))} className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:ring-2 focus:ring-accent focus:outline-none">
                  <option value="">All years</option>
                  {classYears.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">Chapter / Group</label>
                <select data-testid="filter-group" value={filters.group_id} onChange={(e) => setFilters((f) => ({ ...f, group_id: e.target.value }))} className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:ring-2 focus:ring-accent focus:outline-none">
                  <option value="">All chapters</option>
                  {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">State</label>
                <select data-testid="filter-state" value={filters.state} onChange={(e) => setFilters((f) => ({ ...f, state: e.target.value }))} className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:ring-2 focus:ring-accent focus:outline-none">
                  <option value="">All states</option>
                  {states.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">Keyword</label>
                <input data-testid="filter-keyword" type="text" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Name, email, employer…" className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:ring-2 focus:ring-accent focus:outline-none" />
              </div>
            </div>
            {hasFilters && (
              <button data-testid="clear-filters" onClick={clearFilters} className="mt-3 text-xs text-muted-foreground hover:text-foreground underline">Clear filters</button>
            )}
          </div>
          <label data-testid="import-dropzone" className="block bg-card border-2 border-dashed border-border rounded-lg p-12 text-center cursor-pointer hover:border-primary hover:bg-muted/40 transition-colors">
            <UploadCloud className="h-12 w-12 mx-auto text-muted-foreground" />
            <div className="mt-4 font-medium">{busy ? "Parsing file…" : "Click to upload a CSV or Excel file"}</div>
            <div className="text-sm text-muted-foreground mt-1">Supports .csv, .xlsx and .xls</div>
            <input data-testid="import-file-input" type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} disabled={busy} />
          </label>
        </div>
      )}

      {step === 2 && parsed && (
        <div>
          <div className="bg-card border border-border rounded-lg shadow-sm p-5 mb-5 flex items-center gap-3">
            <FileSpreadsheet className="h-6 w-6 text-primary" />
            <div className="text-sm"><b>{parsed.row_count}</b> rows · <b>{parsed.columns.length}</b> columns detected. Match your spreadsheet columns to AlumniOS fields below.</div>
          </div>
          <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
            <div className="grid grid-cols-2 px-4 py-3 border-b border-border text-xs font-mono uppercase tracking-wider text-muted-foreground">
              <div>AlumniOS Field</div><div>Spreadsheet Column</div>
            </div>
            <div className="divide-y divide-border max-h-[420px] overflow-y-auto">
              {FIELDS.map(([field, label]) => (
                <div key={field} className="grid grid-cols-2 px-4 py-2.5 items-center gap-3">
                  <div className="text-sm font-medium">{label}</div>
                  <select data-testid={`map-${field}`} value={mapping[field] || ""} onChange={(e) => setMapping((m) => ({ ...m, [field]: e.target.value }))} className="px-3 py-2 rounded-md border border-input bg-background text-sm focus:ring-2 focus:ring-accent focus:outline-none">
                    <option value="">— Not mapped —</option>
                    {parsed.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-between mt-5">
            <button onClick={() => setStep(1)} className="flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-border text-sm hover:bg-muted"><ArrowLeft className="h-4 w-4" /> Back</button>
            <button data-testid="import-validate" onClick={validate} disabled={busy} className="flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60">{busy ? "Checking…" : "Review"} <ArrowRight className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      {step === 3 && validation && (
        <div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <SummaryCard icon={Users} label="Records" value={validation.record_count} accent="bg-primary/10 text-primary" testid="summary-records" />
            <SummaryCard icon={AlertTriangle} label="Missing Names" value={validation.missing_names} accent="bg-amber-100 text-amber-700" testid="summary-missing" />
            <SummaryCard icon={AlertTriangle} label="Invalid Emails" value={validation.invalid_emails} accent="bg-red-100 text-red-700" testid="summary-invalid-emails" />
            <SummaryCard icon={AlertTriangle} label="Possible Duplicates" value={validation.duplicate_count} accent="bg-orange-100 text-orange-700" testid="summary-duplicates" />
          </div>

          {validation.duplicates.length > 0 && (
            <div className="bg-card border border-border rounded-lg shadow-sm p-5 mb-5">
              <div className="flex items-center gap-2 text-sm font-medium mb-3"><AlertTriangle className="h-4 w-4 text-orange-600" /> Possible duplicates (will NOT be merged automatically)</div>
              <div className="max-h-48 overflow-y-auto text-sm space-y-1">
                {validation.duplicates.map((d) => <div key={d.row} className="flex justify-between border-b border-border py-1"><span>Row {d.row}: {d.name || "Unnamed"}</span><span className="text-muted-foreground">Class {d.class_year ?? "—"}</span></div>)}
              </div>
            </div>
          )}

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-900 flex items-center gap-2 mb-5">
            <CheckCircle2 className="h-4 w-4" /> Records with no name will be skipped. Duplicates are imported as separate records for you to review manually.
          </div>

          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-border text-sm hover:bg-muted"><ArrowLeft className="h-4 w-4" /> Back</button>
            <button data-testid="import-commit" onClick={commit} disabled={busy} className="flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60">{busy ? "Importing…" : `Import ${validation.record_count - validation.missing_names} Records`}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const SummaryCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="bg-card border border-border rounded-lg shadow-sm p-4">
    <div className={`h-9 w-9 rounded-md flex items-center justify-center ${accent}`}><Icon className="h-5 w-5" /></div>
    <div className="mt-3 text-2xl font-serif font-bold">{value}</div>
    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
  </div>
);
